import React from 'react';
import ReactDOM from 'react-dom/client';
import { CART_GLOBAL_VARIABLE_NAME, CART_ELEMENT_ID } from '../../app/cart/constants.js';
import events from '../../app/cart/events.js';
import * as persist from '../../app/cart/persist.js';
import Cart from './Cart.js';
import { documentReady } from '../utils.js';

export { default as events } from '../../app/cart/events.js';
export const state = persist;

const MAX_WAIT_RETRIES = 10;

// Generalized non-blocking wait loop
const waitFor = (conditionFn, limit = MAX_WAIT_RETRIES) => new Promise((resolve, reject) => {
    const check = (fn, retries = 0) => {
        if (conditionFn()) {
            return resolve();
        }
        if (retries >= limit) {
            return reject('Exceeded retry attempt limit');
        }
        retries++;
        setTimeout(() => check(fn, retries), 0);
    };
    check(conditionFn);
});

// In lieu of passing the hosting site url via config, determine based on the `src` url used on the consuming site.
const determineOriginUrl = () => {
    try {
        if (!document.currentScript?.src) {
            throw new Error(`[${CART_GLOBAL_VARIABLE_NAME}] Script attribute \`src\` is missing`);
        }

        return new URL(document.currentScript.src).origin;
    } catch (error) {
        console.error(`[${CART_GLOBAL_VARIABLE_NAME}] Failed to determine origin URL:`, error);
    }
};
const originUrl = determineOriginUrl();

const scriptConfig = (() => {
    try {
        return JSON.parse(document.currentScript?.dataset.config ?? 'null');
    } catch {
        return null;
    }
})();

const createCartElement = (embeddedConfig) => {
    /*
        Using an `aside` element, rather than either a regular `div` or a 'dialog`, because semantically
        the cart is complementary to the host page's main function.

        Deliberately left unnamed to avoid doubling up for screen readers —
        the panel inside carries the accessible name via `aria-labelledby`. , TODO: should the wrapper be the aside?

        @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/aside
    */
    const element = document.createElement('aside');
    element.id = CART_ELEMENT_ID;
    document.body.prepend(element);

    const root = ReactDOM.createRoot(element);
    root.render(<Cart config={embeddedConfig} />);
};

const loadPricing = async (baseUrl) => {
    const catalog = await fetch(`${baseUrl}/api/products/catalog`).then(res => res.json());
    // wait until storage location is available
    await waitFor(() => window[CART_GLOBAL_VARIABLE_NAME]?.isReady);
    window[CART_GLOBAL_VARIABLE_NAME].pricingCatalog = catalog;

    persist.updatePersist({
        catalog,
    });

    events.pricingLoaded.emit();
};

async function init() {
    console.log(`[${CART_GLOBAL_VARIABLE_NAME}] Initializing... \`originUrl\`: ${originUrl}`);
    const persistedData = persist.getPersist();

    const preInitConfig = scriptConfig ?? {};

    const config = {
        originUrl, // Basically just for record-keeping - originUrl shouldn't be used except as a fallback, below
        sourceUrl: preInitConfig.sourceUrl ?? originUrl,
        checkoutUrl: preInitConfig.checkoutUrl,
        excludedPaths: preInitConfig.excludedPaths ?? [],
        amazon: preInitConfig.amazon ?? {},
        // Host-supplied design token overrides. Passed through raw — validation
        // against the token allowlist happens in `tokens.js`, close to the schema
        // it validates against.
        tokens: preInitConfig.tokens,
        totalQuantity: persist.utils.getTotalQuantity(persistedData?.products),
        isCartPanelOpen: persistedData?.isCartPanelOpen || false,
    };

    if (!preInitConfig.sourceUrl) {
        console.warn(`[${CART_GLOBAL_VARIABLE_NAME}] No \`sourceUrl\` was provided, falling back to \`originUrl\` (${originUrl}).`);
    }

    createCartElement(config);
    events.ready.emit({ sourceUrl: config.sourceUrl });
}

// Get pricing loading early to reduce display delay.
// Because this function is async, it should complete after window[CART_GLOBAL_VARIABLE_NAME] exists.
// This is a known race condition where we're hoping to take advantage of the async delay. It should run after window[CART_GLOBAL_VARIABLE_NAME] exists.
events.ready.subscribe((args) => {
    if (args?.sourceUrl) {
        loadPricing(args.sourceUrl);
    }
});

events.ready.subscribe(async () => {
    try {
        // Wait for window[CART_GLOBAL_VARIABLE_NAME] to be initialized.
        await waitFor(() => window[CART_GLOBAL_VARIABLE_NAME], 10);
        window[CART_GLOBAL_VARIABLE_NAME].isReady = true;
    } catch (err) {
        console.error(`[${CART_GLOBAL_VARIABLE_NAME}] ${err}`);
    }
});

documentReady(init);
